/**
 * Publishing Status Component — Domain interfaces (MVP).
 */

// ── Platform & Status Enums ────────────────────────────────

/**
 * Supported distribution platforms.
 * Extensible — new platforms can be added as business needs evolve.
 */
export enum PublishingPlatform {
  YouTube = 'YouTube',
  Facebook = 'Facebook',
  Instagram = 'Instagram',
  Twitter = 'Twitter',
  TikTok = 'TikTok',
  FranceTv = 'France.tv',
}

/**
 * Distribution event lifecycle statuses.
 */
export enum PublishingStatus {
  Success = 'Success',
  SuccessWithWarnings = 'SuccessWithWarnings',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
  Scheduled = 'Scheduled',
  Rendering = 'Rendering',
  Processing = 'Processing',
  PostUploadProcessing = 'PostUploadProcessing',
  Draft = 'Draft',
}

/**
 * Date range filter key for the publishing status toolbar dropdown.
 */
export type PublishingDateRangeKey = 'all' | 'yesterday' | 'today' | 'last7' | 'last30';

// ── Event Model ────────────────────────────────────────────

/**
 * A single distribution/publishing event.
 * Represents one content item distributed to one platform.
 */
export interface PublishingEvent {
  id: string;
  clipTitle?: string;
  platform?: PublishingPlatform;
  account?: string;
  status: PublishingStatus;
  /** API `created_at` (stored as UTC; format for display in user timezone). */
  createdAt?: string;
  /** @deprecated Use {@link createdAt} with timezone-aware formatting. */
  publishedDate?: string;
  /** @deprecated Use {@link createdAt} with timezone-aware formatting. */
  publishedTime?: string;
  thumbnail: string;
  errorMessage?: string;
  external_url?: string;
  /** MAM media id after Export to MAM; null when not imported. */
  media_id: number | null;
  /** URL of the clipping studio edit page where the publish was created (for Re-edit). */
  sourceEditUrl?: string;
}

// ── SignalR Messages ───────────────────────────────────────

export interface PublishingStatusUpdateMessage {
  event_id: string;
  new_status: PublishingStatus;
  error_message?: string;
  timestamp: string;
}

export interface BulkDistributionFinishedMessage {
  job_id: string;
  success_count: number;
  total_count: number;
}

/** States allowed for Export to MAM (backend allow-list: 110 / 120). */
export function isExportToMamEligible(status: PublishingStatus): boolean {
  return (
    status === PublishingStatus.Success ||
    status === PublishingStatus.SuccessWithWarnings
  );
}

/**
 * UI statuses where Cancel may be shown (optimistic).
 * Backend remains authoritative (states 10 / 40 / 50 / 60).
 */
export function isCancelEligible(status: PublishingStatus): boolean {
  return (
    status === PublishingStatus.Rendering ||
    status === PublishingStatus.Processing
  );
}
